import React, { useMemo } from "react";
import CompanyRow from "@/components/sidebar/CompanyRow";
import { Workspace, WorkspaceCompany } from "@/types/workspace";

interface WorkspaceSectionProps {
  workspace: Workspace;
  activeCompanyId: number | null;
  onCompanyClick: (workspace: Workspace, company: WorkspaceCompany) => void;
}

const WorkspaceSection: React.FC<WorkspaceSectionProps> = ({
  workspace,
  activeCompanyId,
  onCompanyClick,
}) => {
  const hasCompanies = workspace.companies.length > 0;
  // Fixed alphabetical order, so a company sits in the same place every time you
  // open this list. It used to hoist whichever company was active to the top —
  // which reshuffled the list on every switch. That was tolerable while
  // `activeCompanyId` was usually null (it only resolved on /reports/:id), but
  // it now always resolves, so the hoist fired constantly. The active company is
  // marked by `active` on its row; it doesn't need to move as well.
  //
  // Sorting by name also solves what the hoist was for — the API appends new
  // companies last, so a freshly-created one was buried at the bottom.
  const orderedCompanies = useMemo(
    () =>
      [...workspace.companies].sort((a, b) =>
        a.company_name.localeCompare(b.company_name),
      ),
    [workspace.companies],
  );

  return (
    <section className="space-y-1">
      <p className="px-2 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--vb-text-3)]">
        {workspace.name}
      </p>

      {hasCompanies ? (
        <div className="space-y-1">
          {orderedCompanies.map((company) => (
            <CompanyRow
              key={`${workspace.id}-${company.company_id}`}
              company={company}
              active={activeCompanyId === company.company_id}
              onClick={(clickedCompany) => onCompanyClick(workspace, clickedCompany)}
            />
          ))}
        </div>
      ) : (
        <p className="px-2 py-1 text-xs text-[color:var(--vb-text-3)]">–</p>
      )}
    </section>
  );
};

export default WorkspaceSection;
